import { FormattedMessage, useIntl } from 'react-intl';

import FallbackContent from '~/components/Shared/FallbackContent/FallbackContent';
import { Icon } from '~/eds/src/components/Icon/Icon';
import { Link } from '~/eds/src/components/Link/Link';
import { IconColor } from '~/eds/src/types/IconColor';
import { IconType } from '~/eds/src/types/IconType';
import { HttpStatusCodeType } from '~/enums/HttpStatusCodeType';
import { QueryParamType } from '~/enums/QueryParamType';
import { api } from '~/redux/api/createApi';
import { coerceRtkqError } from '~/redux/api/utils';
import { useRouting } from '~/routing/useRouting';

const Page = () => {
  const intl = useIntl();
  const { getSearchParam } = useRouting();
  const documentId = getSearchParam(QueryParamType.DocumentId);
  const versionId = getSearchParam(QueryParamType.VersionId);
  const { data, error, isFetching } =
    api.endpoints.downloadTicketDocumentVersionEmailAttachment.useQuery({
      documentId,
      versionId,
    });
  const status = coerceRtkqError(error)?.response?.status;
  const notFoundDocumentVersionId = status === HttpStatusCodeType.NotFound;

  function generateContent(icon: IconType, iconColor: IconColor, content: any) {
    return (
      <FallbackContent
        icon={<Icon color={iconColor} icon={icon} size="l" />}
        title={content}
      />
    );
  }

  if (isFetching) {
    return generateContent(
      'loading',
      'status.info',
      intl.formatMessage({
        id: 'DownloadEmailAttachment.verifyingMessage',
        defaultMessage: 'Your download link is being verified.',
        description: 'Message shown while verifying download link in progress',
      }),
    );
  } else if (notFoundDocumentVersionId) {
    return generateContent(
      'status-reject',
      'status.danger',
      intl.formatMessage({
        id: 'DownloadEmailAttachment.linkNotExistMessage',
        defaultMessage: 'Your download link does not exist.',
        description: 'Error message when download link does not exist',
      }),
    );
  } else {
    const url = data?.url;
    if (url) {
      const btn = (
        <FormattedMessage
          id="DownloadEmailAttachment.downloadButton"
          defaultMessage="<link>Click here to download attachment</link>"
          description="Button text for downloading attachment"
          values={{
            link: (chunks) => (
              <Link variant="button" href={url}>
                {chunks}
              </Link>
            ),
          }}
        />
      );
      return generateContent('status-success', 'status.success', btn);
    } else {
      return generateContent(
        'status-reject',
        'status.danger',
        intl.formatMessage({
          id: 'DownloadEmailAttachment.linkNotFoundMessage',
          defaultMessage:
            'Your download link is not found. Please re-check your email.',
          description: 'Error message when download link is not found',
        }),
      );
    }
  }
};

export default Page;
